import React, { useState, type FormEvent } from "react";

export interface RegisterFormValues {
  username: string;
  email: string;
  password: string;
}

export interface RegisterFormProps {
  onSubmit: (values: RegisterFormValues) => void | Promise<void>;
  loading?: boolean;
  error?: string | null;
  className?: string;
  usernameLabel?: string;
  emailLabel?: string;
  /** Minimum password length enforced client-side before submit. Default 8. */
  minPasswordLength?: number;
  /** Show a "confirm password" field that must match. Default true. */
  showConfirmPassword?: boolean;
}

interface FieldErrors {
  username?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(
  values: { username: string; email: string; password: string; confirmPassword: string },
  minPasswordLength: number,
  showConfirmPassword: boolean
): FieldErrors {
  const errors: FieldErrors = {};
  if (!values.username.trim()) errors.username = "Choose a username.";
  if (!values.email.trim()) {
    errors.email = "Enter your email address.";
  } else if (!EMAIL_PATTERN.test(values.email.trim())) {
    errors.email = "Enter a valid email address.";
  }
  if (!values.password) {
    errors.password = "Choose a password.";
  } else if (values.password.length < minPasswordLength) {
    errors.password = `Password must be at least ${minPasswordLength} characters.`;
  }
  if (showConfirmPassword && values.confirmPassword !== values.password) {
    errors.confirmPassword = "Passwords don't match.";
  }
  return errors;
}

/** Headless-friendly registration form. Bring your own container via `className`; every element carries a stable `fka-*` class for restyling. */
export function RegisterForm({
  onSubmit,
  loading = false,
  error,
  className,
  usernameLabel = "Username",
  emailLabel = "Email address",
  minPasswordLength = 8,
  showConfirmPassword = true,
}: RegisterFormProps) {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const errors = validate({ username, email, password, confirmPassword }, minPasswordLength, showConfirmPassword);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;
    await onSubmit({ username: username.trim(), email: email.trim(), password });
  };

  return (
    <form className={className ?? "fka-register-form flex flex-col gap-4"} onSubmit={handleSubmit} noValidate>
      <div className="fka-field flex flex-col gap-1">
        <label htmlFor="fka-register-username" className="fka-label text-sm font-medium text-slate-300">
          {usernameLabel}
        </label>
        <input
          id="fka-register-username"
          name="username"
          type="text"
          autoComplete="username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          aria-invalid={Boolean(fieldErrors.username)}
          aria-describedby={fieldErrors.username ? "fka-register-username-error" : undefined}
          className="fka-input rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100 outline-none focus:border-amber-500"
        />
        {fieldErrors.username && (
          <p id="fka-register-username-error" className="fka-field-error text-sm text-red-400">
            {fieldErrors.username}
          </p>
        )}
      </div>

      <div className="fka-field flex flex-col gap-1">
        <label htmlFor="fka-register-email" className="fka-label text-sm font-medium text-slate-300">
          {emailLabel}
        </label>
        <input
          id="fka-register-email"
          name="email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-invalid={Boolean(fieldErrors.email)}
          aria-describedby={fieldErrors.email ? "fka-register-email-error" : undefined}
          className="fka-input rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100 outline-none focus:border-amber-500"
        />
        {fieldErrors.email && (
          <p id="fka-register-email-error" className="fka-field-error text-sm text-red-400">
            {fieldErrors.email}
          </p>
        )}
      </div>

      <div className="fka-field flex flex-col gap-1">
        <label htmlFor="fka-register-password" className="fka-label text-sm font-medium text-slate-300">
          Password
        </label>
        <input
          id="fka-register-password"
          name="password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          aria-invalid={Boolean(fieldErrors.password)}
          aria-describedby={fieldErrors.password ? "fka-register-password-error" : undefined}
          className="fka-input rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100 outline-none focus:border-amber-500"
        />
        {fieldErrors.password && (
          <p id="fka-register-password-error" className="fka-field-error text-sm text-red-400">
            {fieldErrors.password}
          </p>
        )}
      </div>

      {showConfirmPassword && (
        <div className="fka-field flex flex-col gap-1">
          <label htmlFor="fka-register-confirm-password" className="fka-label text-sm font-medium text-slate-300">
            Confirm password
          </label>
          <input
            id="fka-register-confirm-password"
            name="confirmPassword"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            aria-invalid={Boolean(fieldErrors.confirmPassword)}
            aria-describedby={fieldErrors.confirmPassword ? "fka-register-confirm-password-error" : undefined}
            className="fka-input rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100 outline-none focus:border-amber-500"
          />
          {fieldErrors.confirmPassword && (
            <p id="fka-register-confirm-password-error" className="fka-field-error text-sm text-red-400">
              {fieldErrors.confirmPassword}
            </p>
          )}
        </div>
      )}

      {error && (
        <p role="alert" className="fka-server-error text-sm text-red-400">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={loading}
        className="fka-submit rounded-md bg-amber-500 px-4 py-2 font-medium text-slate-950 transition disabled:cursor-not-allowed disabled:opacity-60 hover:bg-amber-400"
      >
        {loading ? "Creating account…" : "Create account"}
      </button>
    </form>
  );
}
